/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import Constants from '../components/OriginDestinationSelector/constants';
import { StationDataObject } from '../types/FlightSearch.types';
import { LocalizedAirportResponse } from '../types/nearestAirportsCodes.types';
import { useNearestAirportsCodes } from './useNearestAirportsCodes';
import { getApiPlatform } from '../util/utils';

// The nearest-airport API already returns localized, display-ready airport
// data (name, country, type, code) - no need to re-resolve codes against the
// static station list.
const mapLocalizedAirportToStationDataObject = (
  airport: LocalizedAirportResponse,
): StationDataObject => ({
  StationType: airport.stationType,
  StationCode: airport.stationCode,
  StationName: airport.airportName,
  AirportName: airport.airportName,
  CountryName: airport.countryName,
  CityName: '',
  CityCode: '',
  CountryCode: '',
  IsCity: airport.stationType === Constants.CITY_STATION_TYPE,
});

export const useNearestAirportsStations = (
  screenName: string = getApiPlatform(),
) => {
  const {
    data: nearestAirportsCodesData,
    isLoading,
    isError,
    isSuccess,
  } = useNearestAirportsCodes(screenName);

  const nearestStations: StationDataObject[] = useMemo(() => {
    if (!isSuccess) return [];

    return (nearestAirportsCodesData?.airports ?? []).map(
      mapLocalizedAirportToStationDataObject,
    );
  }, [isSuccess, nearestAirportsCodesData?.airports]);

  return {
    data: nearestStations,
    isLoading,
    isError,
  };
};
